import * as React from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $applyNodeReplacement,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  DecoratorNode,
  type NodeKey,
  type EditorConfig,
  type DOMConversionMap,
  type DOMExportOutput,
  type SerializedLexicalNode,
  type Spread,
} from "lexical";
import { $setBlocksType, $patchStyleText } from "@lexical/selection";
import {
  HeadingNode,
  $createHeadingNode,
  QuoteNode,
  $createQuoteNode,
  type HeadingTagType,
} from "@lexical/rich-text";
import {
  ListItemNode,
  ListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { LinkNode, AutoLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import styled, { createGlobalStyle } from "styled-components";
import { uploadEditorImageApi } from "src/api/storage/storageApi";
import { snackbarStore } from "src/store/snackbarStore";

// ─── ImageNode ─────────────────────────────────────────────────────────────────

type SerializedImageNode = Spread<
  { src: string; altText: string; type: "image"; version: 1 },
  SerializedLexicalNode
>;

class ImageNode extends DecoratorNode<React.JSX.Element> {
  __src: string;
  __altText: string;

  static getType(): string {
    return "image";
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__altText, node.__key);
  }

  constructor(src: string, altText: string, key?: NodeKey) {
    super(key);
    this.__src = src;
    this.__altText = altText;
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const div = document.createElement("div");
    return div;
  }

  updateDOM(): false {
    return false;
  }

  isInline(): false {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const img = document.createElement("img");
    img.src = this.__src;
    img.alt = this.__altText;
    img.style.maxWidth = "100%";
    return { element: img };
  }

  static importDOM(): DOMConversionMap {
    return {
      img: () => ({
        conversion: (node: HTMLElement) => {
          const imgEl = node as HTMLImageElement;
          return { node: new ImageNode(imgEl.src, imgEl.alt || "") };
        },
        priority: 0,
      }),
    };
  }

  static importJSON(serialized: SerializedImageNode): ImageNode {
    return new ImageNode(serialized.src, serialized.altText);
  }

  exportJSON(): SerializedImageNode {
    return { type: "image", version: 1, src: this.__src, altText: this.__altText };
  }

  decorate(): React.JSX.Element {
    return (
      <img
        src={this.__src}
        alt={this.__altText}
        style={{ maxWidth: "100%", display: "block", margin: "8px 0" }}
      />
    );
  }
}

function $createImageNode(src: string, altText = ""): ImageNode {
  return $applyNodeReplacement(new ImageNode(src, altText));
}

// ─── HtmlSyncPlugin ────────────────────────────────────────────────────────────

function HtmlSyncPlugin({
  value,
  onChange,
}: {
  value?: string;
  onChange?: (html: string) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const lastEmittedRef = React.useRef<string>("");
  const mountedRef = React.useRef(false);
  // applyHtml 실행 중 update listener 에서 onChange 방지
  const isApplyingRef = React.useRef(false);

  const applyHtml = React.useCallback(
    (html: string) => {
      isApplyingRef.current = true;
      editor.update(
        () => {
          const root = $getRoot();
          root.clear();
          if (html) {
            const parser = new DOMParser();
            const dom = parser.parseFromString(html, "text/html");
            const nodes = $generateNodesFromDOM(editor, dom);
            // root.select() + $insertNodes 는 빈 root 에서 블록 노드 삽입 시 실패할 수 있어
            // root.append 로 직접 삽입
            nodes.forEach((node) => root.append(node));
          }
          if (root.isEmpty()) {
            root.append($createParagraphNode());
          }
        },
        {
          onUpdate: () => {
            isApplyingRef.current = false;
          },
        }
      );
    },
    [editor]
  );

  React.useEffect(() => {
    const incoming = value ?? "";
    if (!mountedRef.current) {
      mountedRef.current = true;
      lastEmittedRef.current = incoming;
      if (incoming) applyHtml(incoming);
      return;
    }
    if (incoming === lastEmittedRef.current) return;
    lastEmittedRef.current = incoming;
    applyHtml(incoming);
  }, [value, applyHtml]);

  // 300ms 디바운스 - 키 입력마다 HTML 직렬화 방지
  React.useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const unregister = editor.registerUpdateListener(
      ({ editorState, dirtyElements, dirtyLeaves }) => {
        if (dirtyElements.size === 0 && dirtyLeaves.size === 0) return;
        // applyHtml 로 인한 업데이트는 무시
        if (isApplyingRef.current) return;
        clearTimeout(timer);
        timer = setTimeout(() => {
          editorState.read(() => {
            const html = $generateHtmlFromNodes(editor);
            if (html !== lastEmittedRef.current) {
              lastEmittedRef.current = html;
              onChange?.(html);
            }
          });
        }, 300);
      }
    );
    return () => {
      clearTimeout(timer);
      unregister();
    };
  }, [editor, onChange]);

  return null;
}

// ─── ToolbarPlugin ─────────────────────────────────────────────────────────────

type BlockType = "paragraph" | "h1" | "h2" | "h3" | "ul" | "ol" | "quote";

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();

  // 텍스트 서식 상태
  const [isBold, setIsBold] = React.useState(false);
  const [isItalic, setIsItalic] = React.useState(false);
  const [isUnderline, setIsUnderline] = React.useState(false);
  const [isStrikethrough, setIsStrikethrough] = React.useState(false);
  const [isSubscript, setIsSubscript] = React.useState(false);
  const [isSuperscript, setIsSuperscript] = React.useState(false);
  const [blockType, setBlockType] = React.useState<BlockType>("paragraph");

  // 폰트 색상
  const [fontColor, setFontColor] = React.useState("#000000");
  const colorInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        setIsBold(selection.hasFormat("bold"));
        setIsItalic(selection.hasFormat("italic"));
        setIsUnderline(selection.hasFormat("underline"));
        setIsStrikethrough(selection.hasFormat("strikethrough"));
        setIsSubscript(selection.hasFormat("subscript"));
        setIsSuperscript(selection.hasFormat("superscript"));

        const anchor = selection.anchor.getNode();
        const element =
          anchor.getKey() === "root" ? anchor : anchor.getTopLevelElementOrThrow();

        const type = element.getType();
        if (type === "heading") {
          setBlockType((element as HeadingNode).getTag() as BlockType);
        } else if (type === "list") {
          setBlockType((element as ListNode).getListType() === "bullet" ? "ul" : "ol");
        } else if (type === "quote") {
          setBlockType("quote");
        } else {
          setBlockType("paragraph");
        }
      });
    });
  }, [editor]);

  const formatBlock = (type: BlockType) => {
    if (type === "ul") {
      editor.dispatchCommand(
        blockType === "ul" ? REMOVE_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND,
        undefined
      );
      return;
    }
    if (type === "ol") {
      editor.dispatchCommand(
        blockType === "ol" ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND,
        undefined
      );
      return;
    }
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      if (type === "paragraph") {
        $setBlocksType(selection, $createParagraphNode);
      } else if (type === "quote") {
        $setBlocksType(selection, $createQuoteNode);
      } else {
        $setBlocksType(selection, () => $createHeadingNode(type as HeadingTagType));
      }
    });
  };

  const applyFontColor = (color: string) => {
    setFontColor(color);
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) && !selection.isCollapsed()) {
        $patchStyleText(selection, { color });
      }
    });
  };

  const handleInsertImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const res = await uploadEditorImageApi(file);
        if (res.statusCode !== 1 || !res.data?.imageUrl) {
          snackbarStore
            .getState()
            .addSnackbar({ message: res.message || "이미지 업로드 실패", severity: "error" });
          return;
        }
        editor.update(() => {
          const imageNode = $createImageNode(res.data.imageUrl);
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertNodes([imageNode]);
          } else {
            const root = $getRoot();
            root.selectEnd();
            const endSelection = $getSelection();
            if ($isRangeSelection(endSelection)) {
              endSelection.insertNodes([imageNode]);
            } else {
              root.append(imageNode);
            }
          }
        });
      } catch (e: any) {
        snackbarStore
          .getState()
          .addSnackbar({ message: e?.message || "이미지 업로드 오류", severity: "error" });
      }
    };
  };

  const handleInsertLink = () => {
    const url = window.prompt("링크 URL 입력:", "https://");
    if (!url) return;
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, { url, target: "_blank" });
  };

  const btn = (
    active: boolean,
    onMD: (e: React.MouseEvent) => void,
    title: string,
    children: React.ReactNode
  ) => (
    <ToolbarBtn type="button" active={active} onMouseDown={(e) => { e.preventDefault(); onMD(e); }} title={title}>
      {children}
    </ToolbarBtn>
  );

  const cmd = (format: "bold" | "italic" | "underline" | "strikethrough" | "subscript" | "superscript") =>
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);

  return (
    <Toolbar>
      {/* 실행 취소 / 다시 실행 */}
      {btn(false, () => editor.dispatchCommand(UNDO_COMMAND, undefined), "실행 취소 (Ctrl+Z)", "↩")}
      {btn(false, () => editor.dispatchCommand(REDO_COMMAND, undefined), "다시 실행 (Ctrl+Y)", "↪")}

      <Divider />

      {/* 단락 형식 */}
      <ToolbarSelect
        value={blockType === "ul" || blockType === "ol" ? "paragraph" : blockType}
        onChange={(e) => formatBlock(e.target.value as BlockType)}
      >
        <option value="paragraph">본문</option>
        <option value="h1">제목 1</option>
        <option value="h2">제목 2</option>
        <option value="h3">제목 3</option>
        <option value="quote">인용문</option>
      </ToolbarSelect>

      <Divider />

      {/* 글자 서식 */}
      {btn(isBold,          () => cmd("bold"),          "굵게 (Ctrl+B)",    <b>B</b>)}
      {btn(isItalic,        () => cmd("italic"),        "기울임 (Ctrl+I)",  <i>I</i>)}
      {btn(isUnderline,     () => cmd("underline"),     "밑줄 (Ctrl+U)",    <u>U</u>)}
      {btn(isStrikethrough, () => cmd("strikethrough"), "취소선",           <s>S</s>)}
      {btn(isSubscript,     () => cmd("subscript"),     "아래 첨자",        <span>x<sub>2</sub></span>)}
      {btn(isSuperscript,   () => cmd("superscript"),   "위 첨자",          <span>x<sup>2</sup></span>)}

      <Divider />

      {/* 폰트 색상 */}
      <ColorBtn
        type="button"
        title="글자 색상 (텍스트 선택 후 클릭)"
        onMouseDown={(e) => { e.preventDefault(); colorInputRef.current?.click(); }}
      >
        <span style={{ borderBottom: `3px solid ${fontColor}`, lineHeight: 1.2, fontWeight: "bold" }}>A</span>
        <input
          ref={colorInputRef}
          type="color"
          value={fontColor}
          style={{ display: "none" }}
          onChange={(e) => applyFontColor(e.target.value)}
        />
      </ColorBtn>

      <Divider />

      {/* 정렬 */}
      {btn(false, () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left"),    "왼쪽 정렬",  "⬛◀")}
      {btn(false, () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center"),  "가운데 정렬","▶⬛◀")}
      {btn(false, () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right"),   "오른쪽 정렬","▶⬛")}
      {btn(false, () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "justify"), "양쪽 정렬",  "⬜")}

      <Divider />

      {/* 목록 */}
      {btn(blockType === "ul", () => formatBlock("ul"), "글머리 기호",  "• 목록")}
      {btn(blockType === "ol", () => formatBlock("ol"), "번호 목록",    "1. 목록")}

      <Divider />

      {/* 링크 / 이미지 */}
      {btn(false, handleInsertLink,  "링크 삽입",    "링크")}
      {btn(false, handleInsertImage, "이미지 업로드", "이미지")}
    </Toolbar>
  );
}

// ─── Editor Nodes & Theme (모듈 레벨 상수 - HMR 시 클래스 참조 안정화) ──────────

const EDITOR_NODES = [
  HeadingNode,
  QuoteNode,
  ListNode,
  ListItemNode,
  LinkNode,
  AutoLinkNode,
  ImageNode,
];

const editorTheme = {
  paragraph: "lex-paragraph",
  heading: { h1: "lex-h1", h2: "lex-h2", h3: "lex-h3" },
  quote: "lex-quote",
  list: {
    nested: { listitem: "lex-nested-listitem" },
    ol: "lex-list-ol",
    ul: "lex-list-ul",
    listitem: "lex-listitem",
  },
  link: "lex-link",
  text: {
    bold: "lex-bold",
    italic: "lex-italic",
    underline: "lex-underline",
    strikethrough: "lex-strikethrough",
    subscript: "lex-subscript",
    superscript: "lex-superscript",
  },
};

// ─── Main Component ────────────────────────────────────────────────────────────

type Props = {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  height?: number;
};

export default function HtmlEditor({ value, onChange, placeholder, height = 600 }: Props) {
  const initialConfig = React.useMemo(
    () => ({
      namespace: "HtmlEditor",
      theme: editorTheme,
      onError: (error: Error) => console.error("[HtmlEditor]", error),
      nodes: EDITOR_NODES,
    }),
    []
  );

  return (
    <EditorWrapper>
      <GlobalEditorStyles />
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin />
        <EditorBody height={height}>
          <RichTextPlugin
            contentEditable={<StyledContentEditable />}
            placeholder={<PlaceholderText>{placeholder || "내용을 입력하세요"}</PlaceholderText>}
            ErrorBoundary={LexicalErrorBoundary}
          />
        </EditorBody>
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <HtmlSyncPlugin value={value} onChange={onChange} />
      </LexicalComposer>
    </EditorWrapper>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const GlobalEditorStyles = createGlobalStyle`
  .lex-h1 { font-size: 1.875rem; font-weight: 700; margin: 0.5em 0; }
  .lex-h2 { font-size: 1.5rem;   font-weight: 700; margin: 0.5em 0; }
  .lex-h3 { font-size: 1.25rem;  font-weight: 600; margin: 0.5em 0; }
  .lex-paragraph   { margin: 0 0 6px; }
  .lex-quote       { border-left: 4px solid #d9d9d9; margin: 8px 0; padding: 4px 16px; color: #666; }
  .lex-bold        { font-weight: bold; }
  .lex-italic      { font-style: italic; }
  .lex-underline   { text-decoration: underline; }
  .lex-strikethrough { text-decoration: line-through; }
  .lex-subscript   { font-size: 0.8em; vertical-align: sub; }
  .lex-superscript { font-size: 0.8em; vertical-align: super; }
  .lex-list-ul  { padding-left: 24px; list-style-type: disc;    margin: 6px 0; }
  .lex-list-ol  { padding-left: 24px; list-style-type: decimal; margin: 6px 0; }
  .lex-listitem { margin: 2px 0; }
  .lex-link     { color: #1677ff; text-decoration: underline; cursor: pointer; }
`;

const EditorWrapper = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus-within {
    border-color: #1677ff;
    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 10px;
  background: #fafafa;
  border-bottom: 1px solid #d9d9d9;
`;

const ToolbarSelect = styled.select`
  padding: 3px 8px;
  height: 28px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 13px;
  background: #fff;
  cursor: pointer;
  &:hover { border-color: #1677ff; }
`;

const ToolbarBtn = styled.button<{ active: boolean }>`
  padding: 3px 10px;
  height: 28px;
  border: 1px solid ${({ active }) => (active ? "#1677ff" : "#d9d9d9")};
  border-radius: 4px;
  background: ${({ active }) => (active ? "#e6f4ff" : "#fff")};
  color: ${({ active }) => (active ? "#1677ff" : "#333")};
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  white-space: nowrap;
  &:hover { border-color: #1677ff; color: #1677ff; }
`;

const ColorBtn = styled.button`
  padding: 3px 10px;
  height: 28px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 2px;
  &:hover { border-color: #1677ff; }
`;

const Divider = styled.div`
  width: 1px;
  height: 20px;
  background: #e8e8e8;
  margin: 0 2px;
`;

const EditorBody = styled.div<{ height: number }>`
  position: relative;
  height: ${({ height }) => height}px;
  overflow-y: auto;
  background: #fff;
`;

const StyledContentEditable = styled(ContentEditable)`
  min-height: 100%;
  padding: 16px;
  outline: none;
  font-size: 14px;
  line-height: 1.75;
`;

const PlaceholderText = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  color: #bfbfbf;
  font-size: 14px;
  pointer-events: none;
  user-select: none;
`;
